import * as FileSaver from "file-saver";
import moment from "moment";
import XLSX from "sheetjs-style";

const THEME = {
    titleBar: "302B63",
    border: "1A1A1A",
    metaText: "111111",
    groupHeader: {
        info: "8E86AA",
        sales: "8E86AA",
        converted: "F59E0B",
        cancel: "7A7298",
        reject: "7A7298",
        pending: "4A90C2",
        convertedPct: "F59E0B",
    },
    subHeader: {
        info: "B5ADC8",
        sales: "B5ADC8",
        converted: "FFCC66",
        cancel: "C8C0D8",
        reject: "B8B0C8",
        pending: "8CB4DC",
        convertedPct: "FFCC66",
    },
    dataTint: {
        converted: "FFF2CC",
        pending: "DCEAF7",
        convertedPct: "FFF2CC",
    },
    summary: {
        base: "302B63",
        converted: "E67300",
        pending: "0052CC",
        convertedPct: "E67300",
    },
    rowWhite: "FFFFFF",
    rowGrey: "E6E6E6",
};

const COLUMN_DEFS = [
    { key: "no", label: "NO.", section: "info", align: "center", width: 8 },
    {
        key: "project",
        label: "PROJECT NAME",
        section: "info",
        align: "left",
        width: 36,
    },
    {
        key: "allCases",
        label: "UNITS",
        section: "sales",
        align: "center",
        format: "unit",
        width: 10,
    },
    {
        key: "totalGrossGdv",
        label: "GROSS GDV",
        section: "sales",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "totalNetGdv",
        label: "NET GDV",
        section: "sales",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "convertedCases",
        label: "UNITS",
        section: "converted",
        align: "center",
        format: "unit",
        width: 10,
    },
    {
        key: "convertGrossGdv",
        label: "GROSS GDV",
        section: "converted",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "convertNetGdv",
        label: "NET GDV",
        section: "converted",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "cancelledCases",
        label: "UNITS",
        section: "cancel",
        align: "center",
        format: "unit",
        width: 10,
    },
    {
        key: "cancelledGrossGdv",
        label: "GROSS GDV",
        section: "cancel",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "cancelledNetGdv",
        label: "NET GDV",
        section: "cancel",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "rejectedCases",
        label: "UNITS",
        section: "reject",
        align: "center",
        format: "unit",
        width: 10,
    },
    {
        key: "rejectedGrossGdv",
        label: "GROSS GDV",
        section: "reject",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "rejectedNetGdv",
        label: "LOAN REJECT",
        section: "reject",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "pendingCases",
        label: "SALES",
        section: "pending",
        align: "center",
        format: "unit",
        width: 10,
    },
    {
        key: "pendingGrossGdv",
        label: "GROSS GDV",
        section: "pending",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "pendingNetGdv",
        label: "NET GDV",
        section: "pending",
        align: "center",
        format: "currency",
        width: 16,
    },
    {
        key: "conversionRate",
        label: "CONVERTED + SIGNEDLO PERCENTAGE",
        section: "convertedPct",
        align: "center",
        format: "percent",
        width: 22,
    },
];

const GROUP_HEADERS = [
    { label: "", start: 0, end: 1, section: "info" },
    { label: "SALES", start: 2, end: 4, section: "sales" },
    { label: "CONVERTED + SIGNEDLO", start: 5, end: 7, section: "converted" },
    { label: "CANCEL", start: 8, end: 10, section: "cancel" },
    { label: "LOAN REJECT", start: 11, end: 13, section: "reject" },
    { label: "PENDING", start: 14, end: 16, section: "pending" },
    {
        label: "CONVERTED + SIGNEDLO PERCENTAGE",
        start: 17,
        end: 17,
        section: "convertedPct",
    },
];

const COL_LETTERS = "ABCDEFGHIJKLMNOPQR";

const cellRef = (colIndex, rowNumber) =>
    `${COL_LETTERS[colIndex]}${rowNumber}`;

const thinBorder = {
    top: { style: "thin", color: { rgb: THEME.border } },
    bottom: { style: "thin", color: { rgb: THEME.border } },
    left: { style: "thin", color: { rgb: THEME.border } },
    right: { style: "thin", color: { rgb: THEME.border } },
};

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") {
        return 0;
    }
    const numericValue = typeof value === "number" ? value : parseFloat(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
};

const getDataCellBg = (section, rowIndex, isTotalRow) => {
    if (isTotalRow) {
        if (section === "converted" || section === "convertedPct") {
            return THEME.summary.converted;
        }
        if (section === "pending") {
            return THEME.summary.pending;
        }
        return THEME.summary.base;
    }

    if (THEME.dataTint[section]) {
        return THEME.dataTint[section];
    }

    return rowIndex >= 10 ? THEME.rowGrey : THEME.rowWhite;
};

const cellStyle = ({
    bg,
    color = THEME.metaText,
    bold = false,
    align = "center",
    wrap = true,
    size = 10,
}) => ({
    fill: { fgColor: { rgb: bg } },
    font: {
        name: "Calibri",
        bold,
        sz: size,
        color: { rgb: color },
    },
    alignment: {
        horizontal: align,
        vertical: "center",
        wrapText: wrap,
    },
    border: thinBorder,
});

const setCell = (ws, colIndex, rowNumber, value, style, format) => {
    const ref = cellRef(colIndex, rowNumber);
    const isNumber = typeof value === "number";
    ws[ref] = {
        t: isNumber ? "n" : "s",
        v: isNumber ? value : value ?? "",
        s: style,
    };

    if (format === "currency") {
        ws[ref].z = '"RM"#,##0.00';
    } else if (format === "percent") {
        ws[ref].z = '0.00"%"';
    } else if (format === "unit") {
        ws[ref].z = "0";
    }
};

const styleMergedRange = (ws, startCol, endCol, rowNumber, style) => {
    for (let colIndex = startCol; colIndex <= endCol; colIndex += 1) {
        const ref = cellRef(colIndex, rowNumber);
        if (!ws[ref]) {
            ws[ref] = { t: "s", v: "", s: style };
        } else {
            ws[ref].s = style;
        }
    }
};

const exportPerformanceExcel = async ({
    rows,
    totals,
    dateFrom,
    dateTo,
    directorLabel,
    directorType,
    nameField = "project",
    nameHeader = "PROJECT NAME",
    reportSubtitle = "PD Project Performance",
    filePrefix = "Performance-Report",
}) => {
    try {
        const columnDefs = COLUMN_DEFS.map((col) =>
            col.key === "project" ? { ...col, label: nameHeader } : col
        );

        const formattedFrom = moment(dateFrom).format("DD.MM.YYYY");
        const formattedTo = moment(dateTo).format("DD.MM.YYYY");
        const monthLabel = moment(dateFrom).format("MMMM").toUpperCase();
        const directorSuffix = directorLabel
            ? ` (${directorType || "DIRECTOR"} - ${directorLabel.toUpperCase()})`
            : "";
        const directorName = directorLabel ? directorLabel.toUpperCase() : "";

        const ws = {};
        const merges = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
            { s: { r: 0, c: 9 }, e: { r: 0, c: 17 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 17 } },
            ...GROUP_HEADERS.filter((group) => group.end > group.start).map(
                (group) => ({
                    s: { r: 2, c: group.start },
                    e: { r: 2, c: group.end },
                })
            ),
        ];

        const metaStyleLeft = cellStyle({
            bg: "FFFFFF",
            bold: true,
            align: "left",
            wrap: false,
            size: 12,
        });
        const metaStyleRight = cellStyle({
            bg: "FFFFFF",
            bold: true,
            align: "right",
            wrap: false,
            size: 11,
        });
        const titleStyle = cellStyle({
            bg: THEME.titleBar,
            color: "FFFFFF",
            bold: true,
            align: "center",
            wrap: false,
            size: 13,
        });

        setCell(
            ws,
            0,
            1,
            `${monthLabel} - D&D OVERALL SALES & CONVERSION REPORT`,
            metaStyleLeft
        );
        styleMergedRange(ws, 0, 8, 1, metaStyleLeft);

        setCell(
            ws,
            9,
            1,
            `${reportSubtitle} ${formattedFrom} - ${formattedTo}`,
            metaStyleRight
        );
        styleMergedRange(ws, 9, 17, 1, metaStyleRight);

        setCell(
            ws,
            0,
            2,
            `D&D ' KL HQ - ANNUAL SALES & CONVERSION RATE${directorSuffix}`,
            titleStyle
        );
        styleMergedRange(ws, 0, 17, 2, titleStyle);

        GROUP_HEADERS.forEach((group) => {
            const label =
                group.start === 0 && group.end === 1 && directorName
                    ? directorName
                    : group.label;
            const style = cellStyle({
                bg: THEME.groupHeader[group.section],
                color: "FFFFFF",
                bold: true,
                align: "center",
                size: 10,
            });
            setCell(ws, group.start, 3, label, style);
            styleMergedRange(ws, group.start, group.end, 3, style);
        });

        columnDefs.forEach((column, colIndex) => {
            setCell(
                ws,
                colIndex,
                4,
                column.label,
                cellStyle({
                    bg: THEME.subHeader[column.section],
                    color: THEME.metaText,
                    bold: true,
                    align: "center",
                    size: 9,
                })
            );
        });

        const writeDataRow = (rowData, excelRow, rowIndex, isTotalRow) => {
            columnDefs.forEach((column, colIndex) => {
                const bg = getDataCellBg(column.section, rowIndex, isTotalRow);
                const style = cellStyle({
                    bg,
                    color: isTotalRow ? "FFFFFF" : THEME.metaText,
                    bold: isTotalRow,
                    align: column.align,
                    wrap: column.key === "project",
                    size: 9,
                });

                let value;
                let format = column.format;

                if (column.key === "no") {
                    value = isTotalRow ? "" : rowIndex + 1;
                    format = isTotalRow ? undefined : "unit";
                } else if (column.key === "project") {
                    value = isTotalRow
                        ? "SUMMARY TOTAL"
                        : rowData[nameField] || rowData.project || "";
                } else if (column.format === "currency") {
                    value = toNumber(rowData[column.key]);
                } else if (column.format === "percent") {
                    value = toNumber(rowData[column.key]);
                } else if (column.format === "unit") {
                    value = toNumber(rowData[column.key]);
                } else {
                    value = rowData[column.key] ?? "";
                }

                setCell(ws, colIndex, excelRow, value, style, format);
            });
        };

        rows.forEach((row, index) => {
            writeDataRow(row, 5 + index, index, false);
        });

        const lastDataRow = 4 + rows.length;
        let lastRow = lastDataRow;

        if (totals) {
            lastRow = lastDataRow + 1;
            writeDataRow(totals, lastRow, rows.length, true);
        }

        ws["!merges"] = merges;
        ws["!cols"] = columnDefs.map((column) => ({ wch: column.width }));
        ws["!rows"] = [
            { hpt: 18 },
            { hpt: 24 },
            { hpt: 20 },
            { hpt: 28 },
            ...Array.from({ length: lastRow - 4 }, () => ({ hpt: 18 })),
        ];
        ws["!ref"] = `A1:${cellRef(columnDefs.length - 1, lastRow)}`;

        const wb = {
            Sheets: { Performance: ws },
            SheetNames: ["Performance"],
        };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });

        FileSaver.saveAs(
            data,
            `${filePrefix}-${moment(dateFrom).format("YYYY-MM-DD")}-${moment(
                dateTo
            ).format("YYYY-MM-DD")}.xlsx`
        );
    } catch (error) {
        console.error("Error generating performance Excel:", error);
        alert("Error generating Excel. Please try again.");
    }
};

export default exportPerformanceExcel;
